<script setup>
import { ref, onMounted, onUnmounted, defineProps, defineEmits, computed, getCurrentInstance } from 'vue'
import { InvoiceType } from '../types.js'
import { ChevronDownIcon } from './Icons.js'

const { proxy } = getCurrentInstance()
const t = (...args) => proxy.$t(...args)

const props = defineProps({
	value: {
		type: String,
		required: true,
	},
})

const emit = defineEmits(['change'])

const isOpen = ref(false)
const dropdownRef = ref(null)

const options = computed(() => [
	{
		type: InvoiceType.STANDARD,
		title: t('invoices.type_standard_title'),
		desc: t('invoices.type_standard_desc'),
	},
	{
		type: InvoiceType.PROFORMA,
		title: t('invoices.type_proforma_title'),
		desc: t('invoices.type_proforma_desc'),
	},
	{
		type: InvoiceType.CREDIT_NOTE,
		title: t('invoices.type_credit_note_title'),
		desc: t('invoices.type_credit_note_desc'),
	},
	{
		type: InvoiceType.RECURRING,
		title: t('invoices.type_recurring_title'),
		desc: t('invoices.type_recurring_desc'),
	},
	{
		type: InvoiceType.DEBIT_NOTE,
		title: t('invoices.type_debit_note_title'),
		desc: t('invoices.type_debit_note_desc'),
	},
])

const selectedOption = computed(() =>
	options.value.find(o => o.type === props.value)
)

const handleClickOutside = (event) => {
	if (dropdownRef.value && !dropdownRef.value.contains(event.target)) {
		isOpen.value = false
	}
}

onMounted(() => document.addEventListener('mousedown', handleClickOutside))
onUnmounted(() => document.removeEventListener('mousedown', handleClickOutside))
</script>

<template>
  <div class="relative flex items-center gap-6" ref="dropdownRef">
    <span class="text-sm font-medium text-slate-900">{{ $t('invoices.invoice_type') }}</span>
    <div class="relative w-full max-w-xs">
      <button
        type="button"
        @click="isOpen = !isOpen"
        class="flex items-center justify-between w-full h-10 px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm ring-offset-white focus:outline-none focus:ring-2 focus:ring-blue-500 hover:bg-slate-100 transition-colors"
      >
        <span class="font-medium text-slate-900">{{ selectedOption ? selectedOption.title : $t('invoices.select_type') }}</span>
        <ChevronDownIcon :class="`h-4 w-4 text-slate-500 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`" />
      </button>

      <div v-if="isOpen" class="absolute left-0 z-50 w-[400px] mt-2 origin-top-left bg-white border border-slate-200 rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none overflow-hidden">
        <div class="py-1 max-h-[400px] overflow-auto">
          <button
            v-for="option in options"
            :key="option.type"
            type="button"
            @click="emit('change', option.type); isOpen = false;"
            :class="`group flex flex-col items-start w-full px-4 py-3 text-left hover:bg-slate-50 transition-colors border-b last:border-0 border-slate-50 ${value === option.type ? 'bg-blue-50/50' : ''}`"
          >
            <span :class="`text-sm font-semibold ${value === option.type ? 'text-blue-700' : 'text-slate-900'}`">
              {{ option.title }}
            </span>
            <span class="text-xs text-slate-500 mt-1 leading-relaxed">
              {{ option.desc }}
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
